import React, {FC, useMemo, useState, useCallback, useEffect} from 'react';
import {ScrollView} from 'react-native';
import Box from '@/Atoms/Box';
import CCPackageTable from '@/Atoms/CCPackageTable';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import Pagination from '@/Screens/Games/Pagination';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';
import styles from './styles';

interface RawSubscriptionFromAPI {
  plan_id: number;
  plan_name: string;
  start_date: string;
  end_date: string;
  status?: string;
}

interface Subscription {
  planName: string;
  planId: number | string;
  startDate: string;
  endDate: string;
  status?: string;
}

interface Props {
  profileData: {
    data?: {
      subscription_history?: RawSubscriptionFromAPI | RawSubscriptionFromAPI[];
    };
  };
}

const HEADERS = [
  'Plan ID',
  'Plan Name',
  'Plan Status',
  'Start Date',
  'End Date',
];
const ITEMS_PER_PAGE = 10;

const SubscriptionTab: FC<Props> = ({profileData}) => {
  const {isMobileOrTab} = useMediaQuery();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const raw = profileData?.data?.subscription_history;

  const getStatusLabel = (code?: number | string): string => {
    switch (Number(code)) {
      case 1:
        return 'Active';
      case 2:
        return 'Expired';
      case 3:
        return 'Upcoming';
      case 4:
        return 'Cancelled';
      case 5:
        return 'Paused';
      default:
        return '—';
    }
  };

  const allSubscriptions: Subscription[] = useMemo(() => {
    if (!raw) return [];

    const asArray = Array.isArray(raw) ? raw : [raw];

    return asArray.map<Subscription>(item => ({
      planId: item.plan_id,
      planName: item.plan_name,
      startDate: item.start_date,
      endDate: item.end_date,
      status: getStatusLabel(item.status),
    }));
  }, [raw]);

  // const totalPages = Math.max(
  //   1,
  //   Math.ceil(allSubscriptions.length / ITEMS_PER_PAGE),
  // );

  const currentSubs = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return allSubscriptions.slice(start, start + ITEMS_PER_PAGE);
  }, [allSubscriptions, currentPage]);

  const [tableData, setTableData] = useState<
    Array<
      Array<{
        name: string;
        type: 'text' | 'link' | 'dropdown';
        url?: string;
        options?: string[];
      }>
    >
  >([]);

  function formatDate(dateOnly) {
    const [year, month, day] = dateOnly.split('-');

    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    // function getOrdinalSuffix(day) {
    //   const dayNumber = parseInt(day, 10);
    //   if (dayNumber >= 11 && dayNumber <= 13) return 'th';
    //   switch (dayNumber % 10) {
    //     case 1:
    //       return 'st';
    //     case 2:
    //       return 'nd';
    //     case 3:
    //       return 'rd';
    //     default:
    //       return 'th';
    //   }
    // }
    const monthName = monthNames[parseInt(month, 10) - 1];
    // const ordinalSuffix = getOrdinalSuffix(day);
    return `${day} ${monthName} '${year.slice(2)}`;
  }

  useEffect(() => {
    if (!currentSubs || !Array.isArray(currentSubs)) return;

    const itemsPerPage = 10;
    const totalItems = currentSubs.length;
    setTotalPages(Math.ceil(totalItems / itemsPerPage));

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedSubs = currentSubs.slice(startIndex, endIndex);

    const formattedData = paginatedSubs.map(sub => {
      const {planName, planId, status, startDate, endDate} = sub ?? {};

      const makeCell = (
        label: string,
        value: string | number | undefined | null,
      ) => ({
        type: 'text' as const,
        name: isMobileOrTab ? (
          <Box flexDirection="row" style={styles.tableStyle}>
            <CCText fontSize={FontSizes[15]} fontWeight={600}>
              {label}:{' '}
            </CCText>
            <CCText fontSize={FontSizes[15]}>{value ?? '—'}</CCText>
          </Box>
        ) : (
          <Box style={styles.tableStyle}>
            <Box type="row-flex-start" style={styles.rowFlex}>
              <CCText color={CCColors.Grey.Grey} fontSize={FontSizes[13]}>
                {value ?? '—'}
              </CCText>
            </Box>
          </Box>
        ),
      });

      return [
        makeCell('Plan ID', planId?.toString()),
        makeCell('Plan', planName),
        makeCell('Status', status),
        makeCell('Start date', formatDate(startDate)),
        makeCell('End date', formatDate(endDate)),
      ];
    });

    setTableData(formattedData);
  }, [currentSubs, currentPage, isMobileOrTab]);

  return (
    <ScrollView style={{padding: 20}}>
      <Spacer spacing={vs(16)} />
      <Box
        type={isMobileOrTab ? 'center' : 'row-center-between'}
        style={{marginLeft: isMobileOrTab ? 0 : 30}}>
        <CCText fontSize={24} color={CCColors.Primary.Dark}>
          Caissa Subscription Details
        </CCText>
        {isMobileOrTab && <Spacer spacing={10} />}
      </Box>
      <Spacer spacing={vs(20)} />
      <Box
        style={{
          backgroundColor: isMobileOrTab ? CCColors.White : '',
          borderRadius: isMobileOrTab ? 8 : 0,
        }}>
        {allSubscriptions.length > 0 ? (
          <>
            <Box>
              <CCPackageTable
                flexArr={[1.2, 2, 2, 2, 2]}
                headers={HEADERS}
                tableData={tableData}
                isMargin={false}
              />
            </Box>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              paginate={setCurrentPage}
            />
          </>
        ) : (
          <CCText
            fontSize={FontSizes[15]}
            color={CCColors.Grey.Grey}
            style={{textAlign: 'center'}}>
            No subscription data available.
          </CCText>
        )}
      </Box>
    </ScrollView>
  );
};

export default SubscriptionTab;
